import { useMemo } from "react";
import { Markdown } from "@/components/chat/markdown";
import DetailDrawer from "@/components/agent-workspace/DetailDrawer";
import { useSessionPages } from "./useSessionPages";
import { creditUnit } from "@/components/alloy/alloyView";
import { aggregateUsage, number, dateLabel } from "./sessionData";

const DOLLARS = creditUnit();

export function PageProgress({ resource, label }) {
  return (
    <div className="session-progress" role="status">
      <span>
        {resource.loading
          ? `Loading ${label}… ${resource.pages.length} pages loaded`
          : resource.error
            ? `${label} unavailable. Loaded pages are retained.`
            : !resource.done
              ? `${label} partially loaded`
              : `${label} loaded`}
      </span>
      {resource.loading ? (
        <button
          type="button"
          onClick={resource.cancel}
          aria-label={`Pause ${label.toLowerCase()}`}
        >
          Pause
        </button>
      ) : resource.error ? (
        <button type="button" onClick={resource.load}>
          Retry {label.toLowerCase()}
        </button>
      ) : !resource.done ? (
        <button type="button" onClick={resource.load}>
          Load more {label.toLowerCase()}
        </button>
      ) : null}
    </div>
  );
}
function Savings({ comparison, unit }) {
  const value = comparison.savedUsd;
  if (value == null)
    return <span className="session-muted">Savings unavailable</span>;
  const percent =
    comparison.percentSaved == null
      ? ""
      : ` (${number(Math.round(Math.abs(comparison.percentSaved) * 10) / 10)}%)`;
  return (
    <span className={value > 0 ? "session-saving" : "session-muted"}>
      {value < 0 ? "Alloy costs " : "Alloy saves "}
      {unit.cost(Math.abs(value))}
      {percent}
      {value < 0 ? " more" : ""}
    </span>
  );
}
export default function SessionDetail({ session, clientSlug, api, onClose, unit = DOLLARS }) {
  const usage = useSessionPages(
    api,
    clientSlug,
    "getAgentConversationUsage",
    session.id,
  );
  const transcript = useSessionPages(
    api,
    clientSlug,
    "getAgentConversation",
    session.id,
  );
  const summary = useMemo(() => aggregateUsage(usage.pages), [usage.pages]);
  const coverage = usage.pages[0]?.coverage;
  const metadata =
    transcript.pages.find((page) => page.session)?.session || session;
  const complete =
    usage.done &&
    usage.pages.at(-1)?.coverage?.complete &&
    !summary.inconsistent &&
    !usage.pages.some((page) => page.coverage?.errors?.length);
  const comparisons = summary.inconsistent ? [] : summary.comparisons;
  const full = comparisons.filter(
    (item) =>
      item.estimatedCostUsd != null &&
      item.coveredActualCostUsd != null &&
      item.excludedRecords === 0 &&
      item.coveredRequests === summary.totals.requestCount,
  );
  const maximum = Math.max(
    summary.totals.actualCostUsd || 0,
    ...full.map((item) => item.estimatedCostUsd),
    0.000001,
  );
  const messages = transcript.pages.flatMap((page) => page.messages);
  const transcriptComplete =
    transcript.done &&
    transcript.pages.at(-1)?.coverage?.complete &&
    !transcript.pages.some((page) => page.coverage?.truncated);
  return (
    <DetailDrawer
      title={metadata.title || `Session ${session.id}`}
      subtitle={`${metadata.source || "Agent/API"} · ${clientSlug} · Read-only conversation`}
      onClose={onClose}
    >
      <div className="session-detail">
        <section aria-labelledby="session-cost-title">
          <div className="session-section-heading">
            <h2 id="session-cost-title">Conversation costs</h2>
            <span className="session-badge">
              {complete ? "Recorded usage" : "Partial coverage"}
            </span>
          </div>
          <PageProgress resource={usage} label="Costs" />
          {usage.pages.some((page) => page.coverage?.errors?.length) && (
            <p className="session-notice">
              The provider reported incomplete coverage. Only successfully
              recorded requests are shown.
            </p>
          )}
          <p className="session-muted session-small">
            {coverage
              ? `Current connection · ${dateLabel(coverage.since)} – ${dateLabel(coverage.asOf)}`
              : "Costs are available only for provider-retained requests on the current connection."}
          </p>
          <div className="session-cost-cards">
            <div className="session-cost-card session-cost-actual">
              <span>Your cost with Alloy</span>
              <strong>{unit.cost(summary.totals.actualCostUsd)}</strong>
              {summary.totals.actualCostUsd != null && (
                <div className="session-cost-track">
                  <i
                    style={{
                      width: `${Math.max(0, (summary.totals.actualCostUsd / maximum) * 100)}%`,
                    }}
                  />
                </div>
              )}
              <small>
                {number(summary.totals.requestCount)} recorded requests
                {!complete ? " · loaded portion" : ""}
              </small>
            </div>
            {comparisons.map((item) => {
              const fullyCovered = full.includes(item);
              return (
                <div className="session-cost-card" key={item.id}>
                  <span>Estimated {item.name}</span>
                  {/* Full estimates only: a partial sum would undercount the model. */}
                  <strong>
                    {unit.cost(fullyCovered ? item.estimatedCostUsd : null)}
                  </strong>
                  {fullyCovered ? (
                    <>
                      <div className="session-cost-track">
                        <i
                          style={{
                            width: `${(item.estimatedCostUsd / maximum) * 100}%`,
                          }}
                        />
                      </div>
                      <Savings comparison={item} unit={unit} />
                    </>
                  ) : (
                    <small className="session-partial">
                      Full estimate unavailable:{" "}
                      {number(item.coveredRequests)} of{" "}
                      {number(summary.totals.requestCount)} requests could be
                      priced.
                    </small>
                  )}
                </div>
              );
            })}
          </div>
          {summary.inconsistent && (
            <p role="alert" className="session-notice">
              Repeated generation IDs were returned across pages. Charges are
              deduplicated; comparisons are withheld. Refresh costs to retry.
            </p>
          )}
          {!comparisons.length && !usage.loading && (
            <p className="session-muted session-small">
              Comparison prices are unavailable. Recorded charges remain
              available independently.
            </p>
          )}
          {summary.unknownCostRecords > 0 && (
            <p className="session-muted session-small">
              {number(summary.unknownCostRecords)} requests have unavailable
              cost. Known charge subtotal:{" "}
              {unit.cost(
                summary.records.length > summary.unknownCostRecords
                  ? summary.knownActualCostUsd
                  : null,
              )}
              .
            </p>
          )}
          <dl className="session-token-grid">
            <div>
              <dt>Input tokens</dt>
              <dd>{number(summary.totals.inputTokens)}</dd>
            </div>
            <div>
              <dt>Output tokens</dt>
              <dd>{number(summary.totals.outputTokens)}</dd>
            </div>
            <div>
              <dt>Cached input</dt>
              <dd>{number(summary.totals.cachedTokens)}</dd>
            </div>
          </dl>
          <div className="session-models">
            {[
              ...new Set(
                summary.records.map((row) => row.model).filter(Boolean),
              ),
            ].map((model) => (
              <span key={model}>{model}</span>
            ))}
          </div>
          <details className="session-assumptions">
            <summary>How these estimates work</summary>
            <p>
              Same recorded tokens and cache hits, with other input at standard
              public prices. Estimates exclude tool fees and cache-write
              premiums; actual charges include billed extras. Model tokenizers
              and outputs may differ.
            </p>
            <p>
              Input includes context sent again across requests. Cached input is
              already included in input; reasoning is already included in
              output.
            </p>
            {comparisons.map((item) => (
              <p key={item.id}>
                {item.name} · Catalog as of{" "}
                {item.pricedAt
                  ? dateLabel(item.pricedAt)
                  : "unavailable or multiple snapshots"}
                {Object.keys(item.exclusions || {}).length
                  ? ` · Exclusions: ${Object.entries(item.exclusions)
                      .map(
                        ([reason, count]) =>
                          `${reason.replaceAll("_", " ")} (${count})`,
                      )
                      .join(", ")}`
                  : ""}
              </p>
            ))}
            <p>
              Coverage is limited to the current connection and
              provider-retained records. Older or rotated-key usage may not be
              recorded.
            </p>
          </details>
          <button
            className="session-text-button"
            type="button"
            onClick={usage.refresh}
            disabled={usage.loading}
          >
            Refresh costs
          </button>
        </section>
        <section aria-labelledby="session-transcript-title">
          <div className="session-section-heading">
            <h2 id="session-transcript-title">Conversation</h2>
            <span className="session-muted session-small">
              {transcriptComplete
                ? "Available transcript loaded"
                : "Partial / unavailable"}
            </span>
          </div>
          <PageProgress resource={transcript} label="Transcript" />
          {transcript.error && (
            <p data-testid="detail-error" className="session-notice">
              This transcript is unavailable. Costs are independent and remain
              visible above.
            </p>
          )}
          {!transcript.loading && !transcript.error && !messages.length && (
            <p className="session-muted">
              No displayable messages in the loaded transcript.
            </p>
          )}
          <div data-testid="session-transcript" className="session-messages">
            {messages.map((message, index) => (
              <article
                className={`session-message ${message.role === "user" ? "session-message-user" : ""}`}
                key={`${index}-${message.id || ""}`}
              >
                <header>
                  {message.role === "user" ? "User" : "Agent"}
                  {message.at && <time>{dateLabel(message.at)}</time>}
                </header>
                {message.role === "user" ? <p>{message.text}</p> : <div className="session-message-prose"><Markdown text={message.text} /></div>}
                {message.clipped && (
                  <small>Message shortened by the source.</small>
                )}
              </article>
            ))}
          </div>
          <button
            data-testid="detail-retry"
            className="session-text-button"
            type="button"
            onClick={transcript.refresh}
            disabled={transcript.loading}
          >
            Refresh transcript
          </button>
        </section>
      </div>
    </DetailDrawer>
  );
}
